import { useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import useScriptRef from 'hooks/useScriptRef';
import { dispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import useAuth from 'hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import SpinnerLoader from 'ui-component/Spinner';

// ===========================|| JWT - OTP VERIFICATION ||=========================== //

const JWTOtpVerification = ({
    loginMode,
    currentStepData,
    handleStepChange,
    registerObj,
    ...others
}: {
    loginMode: any;
    currentStepData: any;
    handleStepChange: Function;
    registerObj: any;
    [key: string]: any;
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const scriptedRef = useScriptRef();
    const { register, signupOtp } = useAuth(); // Ensure resendOtp is in your useAuth
    const [resendLoading, setResendLoading] = useState(false);
    const [resendError, setResendError] = useState('');
    console.log('registerObj', registerObj);

    const handleResendOtp = async () => {
        setResendLoading(true);
        setResendError('');

        try {
            const formData = registerObj['signup'].data;
            await signupOtp(formData.phone, '', formData.countrycode, '', 'phone');
            dispatch(
                openSnackbar({
                    open: true,
                    message: 'OTP has been resent successfully!',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: true
                })
            );
        } catch (error: any) {
            setResendError(error?.response?.data?.message || 'Failed to resend OTP');
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <>
            <Formik
                initialValues={{
                    phoneOtp: '',
                    submit: null
                }}
                validationSchema={Yup.object().shape({
                    phoneOtp: Yup.string().max(6).required('Otp is required')
                })}
                onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
                    try {
                        const formData = registerObj['signup'].data;
                        const result = await register(
                            formData.firstName,
                            formData.lastName,
                            formData.phone,
                            formData.email,
                            formData.password,
                            formData.countrycode,
                            formData.token,
                            values.phoneOtp,
                            loginMode,
                            formData.referenceCode ?? '',
                            formData.selfieWithID,
                            formData.idUpload,
                            formData.dob
                        );
                        console.log(result, 'result');
                        if (scriptedRef.current) {
                            setStatus({ success: true });
                            setSubmitting(false);
                            handleStepChange('registerSuccess');
                            setTimeout(() => {
                                navigate('/', { replace: true });
                            }, 1500);
                        }
                    } catch (err: any) {
                        setStatus({ success: false });
                        setErrors({ submit: err.response?.data?.message || 'Something went wrong' });
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
                    <form noValidate onSubmit={handleSubmit} {...others}>
                        <FormControl
                            fullWidth
                            error={Boolean(touched.phoneOtp && errors.phoneOtp)}
                            sx={{ ...theme.typography.customInput }}
                        >
                            <TextField
                                label="Enter OTP"
                                name="phoneOtp"
                                type="text"
                                value={values.phoneOtp}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*'
                                }}
                            />
                            {touched.phoneOtp && errors.phoneOtp && (
                                <FormHelperText error id="otp-error-text">
                                    {errors.phoneOtp}
                                </FormHelperText>
                            )}
                        </FormControl>

                        {errors.submit && (
                            <Box sx={{ mt: 1 }}>
                                <FormHelperText error>{errors.submit}</FormHelperText>
                            </Box>
                        )}

                        {resendError && (
                            <Box sx={{ mt: 1 }}>
                                <FormHelperText error>{resendError}</FormHelperText>
                            </Box>
                        )}

                        <Box
                            sx={{ mt: 2, display: 'flex', flexDirection: 'row', gap: '15px', mb: '15px', justifyContent: 'space-between' }}
                        >
                            <Button
                                disableElevation
                                disabled={isSubmitting}
                                fullWidth
                                size="large"
                                type="submit"
                                variant="contained"
                                color="secondary"
                                sx={{
                                    backgroundColor: theme.palette.secondary.main,
                                    color: '#121212',
                                    boxShadow: 'inherit',
                                    mb: '10px',
                                    fontSize: '16px',
                                    fontWeight: '700',
                                    width: '100%',
                                    '&:hover': {
                                        backgroundColor: theme.palette.secondary.main,
                                        color: '#212121',
                                        boxShadow: 'inherit'
                                    }
                                }}
                            >
                                VERIFY OTP{isSubmitting && <SpinnerLoader />}
                            </Button>

                            <Button
                                disableElevation
                                disabled={isSubmitting || resendLoading}
                                fullWidth
                                size="large"
                                type="button"
                                variant="contained"
                                color="secondary"
                                onClick={handleResendOtp}
                                sx={{
                                    background: 'none !important',
                                    borderWidth: '1px',
                                    borderStyle: 'solid',
                                    mb: '10px',
                                    color: theme.palette.secondary.main,
                                    border: '1px solid ',
                                    borderColor: theme.palette.secondary.main,
                                    boxShadow: 'inherit',
                                    width: '100%',
                                    '&:hover': {
                                        background: 'none !important',
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                        color: theme.palette.secondary.main,
                                        borderColor: theme.palette.secondary.main,
                                        boxShadow: 'inherit'
                                    }
                                }}
                            >
                                Resend OTP{resendLoading && <SpinnerLoader />}
                            </Button>
                        </Box>
                    </form>
                )}
            </Formik>
        </>
    );
};

export default JWTOtpVerification;
